import React from 'react';
import { UseFormReturn, FieldValues } from 'react-hook-form';
import { motion } from 'framer-motion';
import { FileTextIcon, DownloadIcon, UploadIcon } from '@radix-ui/react-icons';

import { Box, Button, Stack, Text, Card, Heading } from '../../index';

interface FormTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  data: unknown;
  fields: string[];
  createdAt: Date;
  updatedAt: Date;
  usageCount: number;
}

interface FormTemplatesProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  templates: FormTemplate[];
  onApplyTemplate: (template: FormTemplate) => void;
  onSaveAsTemplate?: (name: string, description: string) => void;
  onDeleteTemplate?: (templateId: string) => void;
  categories?: string[];
  allowSave?: boolean;
  allowDelete?: boolean;
}

export function FormTemplates<T extends FieldValues>({
  form,
  templates,
  onApplyTemplate,
  onSaveAsTemplate,
  onDeleteTemplate,
  categories = [],
  allowSave = true,
  allowDelete = false,
}: FormTemplatesProps<T>) {
  const [selectedCategory, setSelectedCategory] = React.useState<string>('all');
  const [showSaveDialog, setShowSaveDialog] = React.useState(false);
  const [templateName, setTemplateName] = React.useState('');
  const [templateDescription, setTemplateDescription] = React.useState('');

  const filteredTemplates = React.useMemo(() => {
    if (selectedCategory === 'all') return templates;
    return templates.filter((t) => t.category === selectedCategory);
  }, [templates, selectedCategory]);

  const handleSaveTemplate = () => {
    if (templateName && onSaveAsTemplate) {
      onSaveAsTemplate(templateName, templateDescription);
      setShowSaveDialog(false);
      setTemplateName('');
      setTemplateDescription('');
    }
  };

  const handleApplyTemplate = (template: FormTemplate) => {
    // Apply template data to form
    const currentValues = form.getValues();
    const templateData = { ...currentValues };

    // Only apply fields that exist in the template
    template.fields.forEach((field) => {
      if (field in (template.data as Record<string, unknown>)) {
        (templateData as Record<string, unknown>)[field] = (
          template.data as Record<string, unknown>
        )[field];
      }
    });

    form.reset(templateData);
    onApplyTemplate(template);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" marginBottom="16px">
        <Heading size="md">Form Şablonları</Heading>

        {allowSave && onSaveAsTemplate && (
          <Button
            size="sm"
            variant="outline"
            leftIcon={<DownloadIcon />}
            onClick={() => setShowSaveDialog(true)}
          >
            Şablon Olarak Kaydet
          </Button>
        )}
      </Stack>

      {categories.length > 0 && (
        <Stack direction="row" gap="8px" marginBottom="16px">
          <Button
            size="sm"
            variant={selectedCategory === 'all' ? 'primary' : 'ghost'}
            onClick={() => setSelectedCategory('all')}
          >
            Tümü
          </Button>
          {categories.map((category) => (
            <Button
              key={category}
              size="sm"
              variant={selectedCategory === category ? 'primary' : 'ghost'}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </Button>
          ))}
        </Stack>
      )}

      <Stack gap="12px">
        {filteredTemplates.length === 0 ? (
          <Box
            padding="32px"
            textAlign="center"
            border="1px solid"
            borderStyle="dashed"
            borderColor="#E5E7EB"
            borderRadius="8px"
          >
            <Box margin="0 auto 16px">
              <FileTextIcon width={48} height={48} color="#9CA3AF" />
            </Box>
            <Text color="#6B7280">Henüz şablon bulunmuyor</Text>
          </Box>
        ) : (
          filteredTemplates.map((template, index) => (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card.Root
                padding="16px"
                cursor="pointer"
                transition="all 0.2s"
                _hover={{
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  transform: 'translateY(-2px)',
                }}
                onClick={() => handleApplyTemplate(template)}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="start">
                  <Box flex="1">
                    <Heading size="sm" marginBottom="4px">
                      {template.name}
                    </Heading>
                    <Text fontSize="14px" color="#6B7280" marginBottom="8px">
                      {template.description}
                    </Text>
                    <Stack direction="row" gap="16px">
                      <Text fontSize="12px" color="#9CA3AF">
                        Kategori: {template.category}
                      </Text>
                      <Text fontSize="12px" color="#9CA3AF">
                        Kullanım: {template.usageCount}
                      </Text>
                      <Text fontSize="12px" color="#9CA3AF">
                        Güncelleme: {new Date(template.updatedAt).toLocaleDateString('tr-TR')}
                      </Text>
                    </Stack>
                  </Box>

                  <Stack direction="row" gap="8px">
                    <Button
                      size="sm"
                      colorPalette="blue"
                      leftIcon={<UploadIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApplyTemplate(template);
                      }}
                    >
                      Uygula
                    </Button>

                    {allowDelete && onDeleteTemplate && (
                      <Button
                        size="sm"
                        variant="ghost"
                        colorPalette="red"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteTemplate(template.id);
                        }}
                      >
                        Sil
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Card.Root>
            </motion.div>
          ))
        )}
      </Stack>

      {/* Save Template Dialog */}
      {showSaveDialog && (
        <Box
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          backgroundColor="rgba(0, 0, 0, 0.5)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={1000}
          onClick={() => setShowSaveDialog(false)}
        >
          <Box
            backgroundColor="#FFFFFF"
            borderRadius="8px"
            padding="24px"
            maxWidth="400px"
            width="90%"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <Heading size="md" marginBottom="16px">
              Şablon Olarak Kaydet
            </Heading>

            <Stack gap="16px">
              <Box>
                <Text fontSize="14px" marginBottom="4px">
                  Şablon Adı
                </Text>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Örn: Standart Müşteri Formu"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #E5E7EB',
                  }}
                />
              </Box>

              <Box>
                <Text fontSize="14px" marginBottom="4px">
                  Açıklama
                </Text>
                <textarea
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="Şablon hakkında kısa açıklama"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #E5E7EB',
                    resize: 'vertical',
                  }}
                />
              </Box>

              <Stack direction="row" justifyContent="flex-end" gap="12px">
                <Button variant="ghost" onClick={() => setShowSaveDialog(false)}>
                  İptal
                </Button>
                <Button colorPalette="blue" onClick={handleSaveTemplate} disabled={!templateName}>
                  Kaydet
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// Template management utilities
export const templateUtils = {
  // Export form data as template
  exportTemplate: (
    formData: unknown,
    metadata: { name: string; description: string; category: string }
  ) => {
    const template: FormTemplate = {
      id: `template-${Date.now()}`,
      name: metadata.name,
      description: metadata.description,
      category: metadata.category,
      data: formData,
      fields: Object.keys(formData as Record<string, unknown>),
      createdAt: new Date(),
      updatedAt: new Date(),
      usageCount: 0,
    };

    return template;
  },

  // Import template from JSON
  importTemplate: async (file: File): Promise<FormTemplate> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const template = JSON.parse(e.target?.result as string);
          resolve(template);
        } catch {
          reject(new Error('Invalid template file'));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  },

  // Download template as JSON
  downloadTemplate: (template: FormTemplate) => {
    const dataStr = JSON.stringify(template, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const exportFileDefaultName = `${template.name.toLowerCase().replace(/\s+/g, '-')}-template.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  },

  // Merge template with existing data
  mergeTemplate: (
    currentData: unknown,
    templateData: unknown,
    mode: 'replace' | 'merge' = 'merge'
  ) => {
    if (mode === 'replace') {
      return templateData;
    }

    // Merge mode: keep existing data, only add/update from template
    return {
      ...(currentData as object),
      ...(templateData as object),
    };
  },
};
